import { createPortal } from "react-dom";
import { X, Loader2 } from "lucide-react";

/**
 * GenerationHistorySidebar
 * Slide-in panel showing all previously generated image variants for the
 * current character (sourced from the polling API's `imageVariants` array).
 * Clicking a variant closes the panel and sets that image as the active
 * preview in CreateTab for further editing.
 */
export default function GenerationHistorySidebar({
    show,
    onClose,
    images = [],       // array of imageVariant objects: { _id, originalUrl, source, createdAt }
    loading = false,
    onSelectVariant,   // callback(variantObject)
}) {
    const formatDate = (dateString) => {
        if (!dateString) return "";
        const date = new Date(dateString);
        const now = new Date();
        const diffMs = Math.abs(now - date);
        const diffMins = Math.floor(diffMs / 60000);
        if (diffMins < 1) return "just now";
        if (diffMins < 60) return `${diffMins} min ago`;
        const diffHours = Math.floor(diffMins / 60);
        if (diffHours < 24) return `${diffHours}h ago`;
        const diffDays = Math.floor(diffHours / 24);
        if (diffDays === 1) return "1 day ago";
        if (diffDays < 7) return `${diffDays} days ago`;
        if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
        return date.toLocaleDateString();
    };

    return createPortal(
        <>
            {/* Backdrop */}
            {show && (
                <div
                    className="fixed inset-0 z-[9998] bg-black/30 backdrop-blur-sm"
                    onClick={onClose}
                />
            )}

            {/* Sidebar Panel */}
            <div
                className={`fixed inset-y-0 right-0 pt-2 w-[500px] bg-white
          border-l border-gray-200 shadow-xl transform
          transition-transform duration-300 ease-in-out z-[9999] will-change-transform
          ${show ? "translate-x-0" : "translate-x-full"}`}
            >
                <div className="flex flex-col h-full">
                    {/* HEADER */}
                    <div className="flex items-center justify-between p-6 border-b border-gray-200">
                        <div>
                            <h2 className="text-xl font-semibold text-gray-900">
                                Generation History
                            </h2>
                            <p className="text-sm text-gray-600 mt-1">
                                Your previously generated character scenes
                            </p>
                        </div>

                        <button
                            onClick={onClose}
                            className="p-2 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100"
                        >
                            <X className="h-5 w-5" />
                        </button>
                    </div>

                    {/* GENERATED IMAGES LIST */}
                    <div className="flex-1 overflow-y-auto min-h-0 overscroll-contain scroll-smooth">
                        {loading ? (
                            <div className="flex flex-col items-center justify-center h-64">
                                <Loader2 className="w-8 h-8 text-orange-500 animate-spin mb-3" />
                                <p className="text-gray-500">Loading history...</p>
                            </div>
                        ) : (
                            <div className="p-6 space-y-3">
                                {images.length === 0 ? (
                                    <div className="text-center py-8">
                                        <p className="text-gray-500">
                                            No generated images found.
                                        </p>
                                    </div>
                                ) : (
                                    images?.map((image) => (
                                        <button
                                            key={image._id}
                                            onClick={() => {
                                                if (onSelectVariant) onSelectVariant(image);
                                            }}
                                            className="w-full text-left rounded-lg border overflow-hidden flex
                      border-gray-200 hover:border-gray-300 transition-colors hover:shadow-md"
                                        >
                                            <div className="relative w-24 h-32 flex-shrink-0 bg-gray-100">
                                                <img
                                                    src={image.originalUrl}
                                                    alt="Generated character"
                                                    className="w-full h-full object-cover"
                                                />
                                            </div>
                                            <div className="p-3 flex-1 bg-gray-50">
                                                <p className="text-sm font-medium mb-1 text-gray-900">
                                                    Generated Character
                                                </p>
                                                <p className="text-xs mb-2 text-gray-600">
                                                    {image.source === "text-to-image"
                                                        ? "Text to Image"
                                                        : "Custom Generation"}
                                                </p>
                                                <span className="text-xs text-gray-500">
                                                    Generated {formatDate(image.createdAt)}
                                                </span>
                                            </div>
                                        </button>
                                    ))
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </>,
        document.body
    );
}
